import { useCallback, useEffect, useRef as useReactRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiUpload } from '../api/client';
import { useCommitPlan, useMe, useRef } from '../api/hooks';
import { AddressFields, type Address } from '../components/AddressFields';
import { RefSelect } from '../components/RefSelect';
import type { CommitPlanInput, OrchestrationPlan, PlanAddress } from '../api/types';
import { fmtMoney } from '../lib/format';

interface ItemRow { name: string; type: string; unitPrice: string; purchaseCost: string; description: string }
interface LineRow { itemName: string; supplierName: string; qty: string; unitPrice: string; unitCost: string; description: string }

const emptyItem = (): ItemRow => ({ name: '', type: 'Service', unitPrice: '', purchaseCost: '', description: '' });
const emptyLine = (): LineRow => ({ itemName: '', supplierName: '', qty: '1', unitPrice: '', unitCost: '', description: '' });
const emptyCustomer = () => ({ displayName: '', companyName: '', email: '', phone: '', mobile: '', website: '' });

// Only accept ISO yyyy-mm-dd; drop anything else (e.g. an AI-produced "in 30 days") so it can't be committed.
const isoDate = (s?: string) => (s && /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : '');

const addrOrUndef = (a: Address): PlanAddress | undefined => {
  const o: Record<string, string> = {};
  for (const [k, v] of Object.entries(a)) if (typeof v === 'string' && v.trim()) o[k] = v.trim();
  return Object.keys(o).length ? (o as PlanAddress) : undefined;
};

export function CreatePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const me = useMe();
  const commit = useCommitPlan();
  const customers = useRef('customers');
  const items = useRef('items');
  const vendors = useRef('vendors');
  const taxes = useRef('tax-codes');
  const assistantEnabled = me.data?.assistantEnabled ?? false;
  const meReady = !me.isLoading;

  const initialPrompt = (location.state as { prompt?: string } | null)?.prompt ?? '';
  const [prompt, setPrompt] = useState(initialPrompt);
  const [files, setFiles] = useState<File[]>([]);
  const [extracting, setExtracting] = useState(false);
  const [assistError, setAssistError] = useState<string | null>(null);
  const [assistMsg, setAssistMsg] = useState<string | null>(null);
  const [reviewed, setReviewed] = useState(false);
  const fileRef = useReactRef<HTMLInputElement>(null);

  // Review state
  const [customer, setCustomer] = useState(emptyCustomer());
  const [billAddr, setBillAddr] = useState<Address>({});
  const [itemRows, setItemRows] = useState<ItemRow[]>([]);
  const [includeQuote, setIncludeQuote] = useState(false);
  const [lines, setLines] = useState<LineRow[]>([emptyLine()]);
  const [expirationDate, setExpirationDate] = useState('');
  const [taxCodeRef, setTaxCodeRef] = useState('');
  const [notes, setNotes] = useState('');
  const [done, setDone] = useState<string | null>(null);

  // Full replace: the review must reflect ONLY the current plan (a later plan that omits a
  // section must clear the prior plan's customer/quote, never leave them staged for Submit).
  const applyPlan = useCallback((plan: OrchestrationPlan) => {
    const c = plan.customer;
    setCustomer(
      c
        ? { displayName: c.displayName ?? '', companyName: c.companyName ?? '', email: c.email ?? '', phone: c.phone ?? '', mobile: c.mobile ?? '', website: c.website ?? '' }
        : emptyCustomer(),
    );
    setBillAddr(c?.billAddr ?? {});
    setItemRows(
      Array.isArray(plan.items)
        ? plan.items.map((it) => ({
            name: it.name ?? '',
            type: it.type === 'NonInventory' ? 'NonInventory' : 'Service',
            unitPrice: it.unitPrice != null ? String(it.unitPrice) : '',
            purchaseCost: it.purchaseCost != null ? String(it.purchaseCost) : '',
            description: it.description ?? '',
          }))
        : [],
    );
    const q = plan.quote;
    if (q && Array.isArray(q.lines) && q.lines.length) {
      setIncludeQuote(true);
      setLines(
        q.lines.map((l) => ({
          itemName: l.itemName ?? '',
          supplierName: l.supplierName ?? '',
          qty: l.qty != null ? String(l.qty) : '1',
          unitPrice: l.unitPrice != null ? String(l.unitPrice) : '',
          unitCost: l.unitCost != null ? String(l.unitCost) : '',
          description: l.description ?? '',
        })),
      );
      if (q.customerEmail) setCustomer((cur) => ({ ...cur, email: cur.email || q.customerEmail! }));
      setExpirationDate(isoDate(q.expirationDate));
      setNotes(q.notes ?? '');
      setTaxCodeRef(q.taxCodeRef ?? '');
    } else {
      setIncludeQuote(false);
      setLines([emptyLine()]);
      setExpirationDate('');
      setNotes('');
      setTaxCodeRef('');
    }
    setReviewed(true);
  }, []);

  const generate = useCallback(async () => {
    setExtracting(true);
    setAssistError(null);
    setAssistMsg(null);
    setDone(null);
    try {
      const fd = new FormData();
      fd.append('message', prompt);
      files.forEach((f) => fd.append('files', f));
      const res = await apiUpload<{ plan: OrchestrationPlan; message: string }>('/api/orchestrate', fd);
      applyPlan(res.plan);
      setAssistMsg(res.message);
    } catch (e) {
      setAssistError((e as Error).message);
    } finally {
      setExtracting(false);
    }
  }, [prompt, files, applyPlan]);

  // Auto-run once when arriving with a prompt from the home page.
  const autoRan = useReactRef(false);
  useEffect(() => {
    if (!autoRan.current && initialPrompt.trim() && assistantEnabled) {
      autoRan.current = true;
      void generate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assistantEnabled]);

  const setItem = (i: number, patch: Partial<ItemRow>) => setItemRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addItem = () => setItemRows((rs) => [...rs, emptyItem()]);
  const removeItem = (i: number) => setItemRows((rs) => rs.filter((_, idx) => idx !== i));

  const setLine = (i: number, patch: Partial<LineRow>) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLine = () => setLines((ls) => [...ls, emptyLine()]);
  const removeLine = (i: number) => setLines((ls) => (ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls));

  const amountOf = (l: LineRow) => (Number(l.qty) || 0) * (Number(l.unitPrice) || 0);
  const costOf = (l: LineRow) => (Number(l.qty) || 0) * (Number(l.unitCost) || 0);
  const filledLines = lines.filter((l) => l.itemName.trim());
  const quoteTotal = filledLines.reduce((s, l) => s + amountOf(l), 0);
  const quoteCost = filledLines.reduce((s, l) => s + costOf(l), 0);

  const namedItems = itemRows.filter((r) => r.name.trim());
  const trimmedName = customer.displayName.trim();
  const customerReady = trimmedName.length > 0;
  // Does the typed name already exist in QuickBooks? (case-insensitive) -> reuse, don't re-enter details.
  const customerExists = customerReady && (customers.data?.some((c) => c.name.trim().toLowerCase() === trimmedName.toLowerCase()) ?? false);
  const customerIsNew = customerReady && !customerExists;
  const quoteLinesValid = filledLines.length > 0 && filledLines.every((l) => Number(l.qty) > 0 && l.unitPrice !== '' && l.unitCost !== '');
  const quoteReady = includeQuote && quoteLinesValid;
  const quoteBlocked = includeQuote && (!quoteLinesValid || !customerReady);
  const somethingToDo = customerReady || namedItems.length > 0 || quoteReady;
  const canSubmit = somethingToDo && !quoteBlocked && !commit.isPending;

  const submit = () => {
    setDone(null);
    const payload: CommitPlanInput = {};
    if (customerReady) {
      // Existing customer -> just reference it by name (resolveCustomer reuses it). New -> send full details.
      payload.customer = customerExists
        ? { displayName: trimmedName }
        : {
            displayName: trimmedName,
            companyName: customer.companyName.trim() || undefined,
            email: customer.email.trim() || undefined,
            phone: customer.phone.trim() || undefined,
            mobile: customer.mobile.trim() || undefined,
            website: customer.website.trim() || undefined,
            billAddr: addrOrUndef(billAddr),
          };
    }
    if (namedItems.length) {
      payload.items = namedItems.map((r) => ({
        name: r.name.trim(),
        type: r.type === 'NonInventory' ? 'NonInventory' : 'Service',
        unitPrice: r.unitPrice ? Number(r.unitPrice) : undefined,
        purchaseCost: r.purchaseCost ? Number(r.purchaseCost) : undefined,
        description: r.description.trim() || undefined,
      }));
    }
    if (quoteReady) {
      payload.quote = {
        lines: filledLines.map((l) => ({
          itemName: l.itemName.trim(),
          supplierName: l.supplierName.trim() || undefined,
          qty: Number(l.qty),
          unitPrice: Number(l.unitPrice),
          unitCost: Number(l.unitCost),
          description: l.description.trim() || undefined,
        })),
        customerEmail: customer.email.trim() || undefined,
        expirationDate: expirationDate || undefined,
        notes: notes || undefined,
        taxCodeRef: taxCodeRef || undefined,
      };
    }

    commit.mutate(payload, {
      onSuccess: (res) => {
        if (res.quote) {
          navigate(`/quotes/${res.quote.id}`);
        } else if (res.items?.length) {
          navigate('/items');
        } else {
          setDone(res.customer ? 'Customer created in QuickBooks.' : 'Nothing to create.');
        }
      },
    });
  };

  return (
    <>
      <h1>✨ Create</h1>
      <p className="muted" style={{ marginTop: 0 }}>
        Describe what you need — a customer, some items, a quote, or all three — review it, then create the records.
      </p>

      <datalist id="customer-list">{customers.data?.map((c) => <option key={c.id} value={c.name} />)}</datalist>
      <datalist id="item-list">{items.data?.map((it) => <option key={it.id} value={it.name} />)}</datalist>
      <datalist id="vendor-list">{vendors.data?.map((v) => <option key={v.id} value={v.name} />)}</datalist>

      <div className="assistant-card">
        <div className="assistant-head">
          {assistantEnabled
            ? '✨ Master prompt — describe everything at once, or attach an RFQ / email / spec sheet'
            : 'The AI assistant is not configured — fill the review below manually.'}
        </div>
        {assistantEnabled && (
          <>
            <textarea
              rows={3}
              value={prompt}
              placeholder="e.g. “New customer Bearston Law (accounts@bearston.ca, 1 King St W, Hamilton ON). Create an item ‘Discovery Review’ at $250 (cost $150). Quote them 10 hours of it.”"
              onChange={(e) => setPrompt(e.target.value)}
            />
            <div className="assistant-row">
              <input ref={fileRef} type="file" multiple accept=".pdf,image/*,.txt,.csv" onChange={(e) => setFiles(Array.from(e.target.files ?? []))} />
              <button type="button" className="primary" onClick={generate} disabled={extracting || (!prompt.trim() && files.length === 0)}>
                {extracting ? 'Reading…' : 'Generate plan'}
              </button>
            </div>
          </>
        )}
        {assistMsg && <div className="banner ok" style={{ marginTop: '0.75rem' }}>{assistMsg}</div>}
        {assistError && <div className="banner error" style={{ marginTop: '0.75rem' }}>{assistError}</div>}
      </div>

      {(reviewed || (meReady && !assistantEnabled) || Boolean(assistError)) && (
        <>
          {commit.error && <div className="banner error">{(commit.error as Error).message}</div>}
          {done && <div className="banner ok">{done}</div>}

          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0 }}>Customer</h2>
              {includeQuote && <span className="muted" style={{ fontSize: '0.8rem' }}>required for the quote</span>}
            </div>
            <div className="field">
              <label>Name{includeQuote ? ' *' : ''}</label>
              <input list="customer-list" value={customer.displayName} onChange={(e) => setCustomer({ ...customer, displayName: e.target.value })} placeholder="Existing or new" />
            </div>
            {customerExists && <div className="banner ok" style={{ marginTop: '0.5rem' }}>✓ Existing QuickBooks customer — it will be reused (no new details needed).</div>}
            {customerIsNew && (
              <>
                <p className="muted" style={{ fontSize: '0.8rem', margin: '0.4rem 0' }}>New customer — add their details:</p>
                <div className="row cols-2">
                  <div className="field"><label>Company</label><input value={customer.companyName} onChange={(e) => setCustomer({ ...customer, companyName: e.target.value })} /></div>
                  <div className="field"><label>Email</label><input type="email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} /></div>
                </div>
                <div className="row cols-2">
                  <div className="field"><label>Phone</label><input value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} /></div>
                  <div className="field"><label>Mobile</label><input value={customer.mobile} onChange={(e) => setCustomer({ ...customer, mobile: e.target.value })} /></div>
                </div>
                <div className="field"><label>Website</label><input value={customer.website} onChange={(e) => setCustomer({ ...customer, website: e.target.value })} placeholder="acme.ca" /></div>
                <AddressFields legend="Billing address" value={billAddr} onChange={setBillAddr} />
              </>
            )}
            {!customerReady && <p className="muted" style={{ fontSize: '0.8rem', margin: 0 }}>Leave blank to skip creating/attaching a customer.</p>}
          </div>

          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0 }}>Items to create {namedItems.length > 0 && <span className="muted">({namedItems.length})</span>}</h2>
              <button type="button" onClick={addItem}>+ Add item</button>
            </div>
            {includeQuote && filledLines.length > 0 && (
              <p className="muted" style={{ fontSize: '0.8rem', margin: '0.4rem 0' }}>
                The quote's line items ({filledLines.map((l) => l.itemName.trim()).join(', ')}) are created in QuickBooks automatically — you don't need to add them here. Use this section only for extra items that aren't on the quote.
              </p>
            )}
            {itemRows.length === 0 && <p className="muted">No standalone items.</p>}
            {itemRows.map((r, i) => (
              <div className="quote-line" key={i}>
                <div className="row cols-2">
                  <div className="field"><label>Name</label><input list="item-list" value={r.name} onChange={(e) => setItem(i, { name: e.target.value })} placeholder="e.g. Discovery Review" /></div>
                  <div className="field">
                    <label>Type</label>
                    <select value={r.type} onChange={(e) => setItem(i, { type: e.target.value })}>
                      <option value="Service">Service</option>
                      <option value="NonInventory">Non-inventory</option>
                    </select>
                  </div>
                </div>
                <div className="line-nums">
                  <div><label>Sales price</label><input type="number" min="0" step="0.01" value={r.unitPrice} onChange={(e) => setItem(i, { unitPrice: e.target.value })} /></div>
                  <div><label>Cost</label><input type="number" min="0" step="0.01" value={r.purchaseCost} onChange={(e) => setItem(i, { purchaseCost: e.target.value })} /></div>
                  <button type="button" className="link" onClick={() => removeItem(i)}>Remove</button>
                </div>
                <div className="field"><label>Description</label><input value={r.description} onChange={(e) => setItem(i, { description: e.target.value })} /></div>
              </div>
            ))}
          </div>

          <div className="card">
            <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontWeight: 600 }}>
              <input type="checkbox" style={{ width: 'auto' }} checked={includeQuote} onChange={(e) => setIncludeQuote(e.target.checked)} /> Include a quote
            </label>
            {includeQuote && (
              <>
                {lines.map((l, i) => {
                  const margin = amountOf(l) - costOf(l);
                  return (
                    <div className="quote-line" key={i}>
                      <div className="row cols-2">
                        <div className="field"><label>Item / service *</label><input list="item-list" value={l.itemName} onChange={(e) => setLine(i, { itemName: e.target.value })} placeholder="e.g. Discovery Review" /></div>
                        <div className="field"><label>Supplier</label><input list="vendor-list" value={l.supplierName} onChange={(e) => setLine(i, { supplierName: e.target.value })} placeholder="QuickBooks vendor or new" /></div>
                      </div>
                      <div className="line-nums">
                        <div><label>Qty *</label><input type="number" min="0" step="1" value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} /></div>
                        <div><label>Unit price *</label><input type="number" min="0" step="0.01" value={l.unitPrice} onChange={(e) => setLine(i, { unitPrice: e.target.value })} /></div>
                        <div><label>Cost / unit *</label><input type="number" min="0" step="0.01" value={l.unitCost} onChange={(e) => setLine(i, { unitCost: e.target.value })} /></div>
                        <div><label>Amount</label><div className="ro-val">{fmtMoney(amountOf(l))}</div></div>
                        <div><label>Margin</label><div className="ro-val" style={{ color: margin >= 0 ? 'var(--status-invoiced)' : 'var(--status-rejected)' }}>{fmtMoney(margin)}</div></div>
                        <button type="button" className="link" onClick={() => removeLine(i)} disabled={lines.length === 1}>Remove</button>
                      </div>
                    </div>
                  );
                })}
                <button type="button" onClick={addLine}>+ Add line</button>
                <div className="row cols-2" style={{ marginTop: '1rem' }}>
                  <div className="field"><label>Expiration date</label><input type="date" value={expirationDate} onChange={(e) => setExpirationDate(e.target.value)} /></div>
                  <RefSelect label="Sales tax" value={taxCodeRef} onChange={setTaxCodeRef} options={taxes.data} placeholder="— QuickBooks default —" />
                </div>
                <div className="field"><label>Notes (appear on the quote)</label><textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
                <div className="right" style={{ lineHeight: 1.7 }}>
                  <div className="muted">Total cost: {fmtMoney(quoteCost)}</div>
                  <div>
                    <strong>Total: {fmtMoney(quoteTotal)}</strong> ·{' '}
                    <span style={{ color: quoteTotal - quoteCost >= 0 ? 'var(--status-invoiced)' : 'var(--status-rejected)' }}>Margin {fmtMoney(quoteTotal - quoteCost)}</span>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="btn-row">
            <button className="primary" type="button" onClick={submit} disabled={!canSubmit}>
              {commit.isPending ? 'Creating…' : 'Create record'}
            </button>
            {quoteBlocked && <span className="error-text">A customer name and complete line items are required for the quote.</span>}
            {!somethingToDo && !quoteBlocked && <span className="muted">Add a customer, item, or quote line to continue.</span>}
            {somethingToDo && !quoteBlocked && (
              <span className="muted">
                Creates the records now.{includeQuote && ' Pushing the quote to QuickBooks as an estimate is a separate Submit step on the Quotes page.'}
              </span>
            )}
          </div>
        </>
      )}
    </>
  );
}
